// Cliente HTTP de las lecturas multiplanta (PP-63), mismo patron que
// requests/http.ts: token en el header, sin cookies ni redirecciones, y toda
// respuesta por el parser de @ucorp/contracts antes de tocar estado.
//
// Ofertas y viajes siguen el cursor hasta MAX_PAGES paginas de 200: 800 filas
// alcanzan para un proveedor con 10 plantas en un dia. Si quedan mas, la
// respuesta conserva `nextCursor` y la vista avisa que la lista esta cortada.
import {
  ProviderFleetPositionsResponse,
  ProviderMultiSiteOffersResponse,
  ProviderMultiSiteTripsResponse,
  ProviderSlotLoadResponse,
  type ProviderMultiSiteSite,
  type ProviderMultiSiteTrip,
} from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { MultiSiteError, type MultiSitePort } from "./port.js";

const PAGE_SIZE = 200;
const MAX_PAGES = 4;
/** Franja de la carga del Inicio: la que el contrato trae por defecto. */
const SLOT_MINUTES = "30";

/** Lo minimo de un schema de contracts, sin depender de zod desde el portal. */
interface Schema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false };
}

interface Paged {
  sites: ProviderMultiSiteSite[];
  items: ProviderMultiSiteTrip[];
  nextCursor?: string | undefined;
}

export function createHttpMultiSitePort(apiUrl: string, request: typeof fetch = fetch): MultiSitePort {
  const base = apiUrl.replace(/\/+$/, "");

  async function get<T>(session: PortalSession, path: string, schema: Schema<T>): Promise<T> {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.accessToken}` },
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new MultiSiteError("unavailable");
    }
    if (response.status === 401 || response.status === 403) throw new MultiSiteError("access");
    if (!response.ok) throw new MultiSiteError("unavailable");
    const payload: unknown = await response.json().catch(() => undefined);
    const parsed = schema.safeParse(payload);
    if (!parsed.success) throw new MultiSiteError("unavailable");
    return parsed.data;
  }

  async function paged<T extends Paged>(session: PortalSession, path: string, schema: Schema<T>, query: URLSearchParams): Promise<T> {
    const items: ProviderMultiSiteTrip[] = [];
    let first: T | undefined;
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const params = new URLSearchParams(query);
      params.set("limit", String(PAGE_SIZE));
      if (cursor !== undefined) params.set("cursor", cursor);
      const data = await get(session, `${path}?${params.toString()}`, schema);
      first ??= data;
      items.push(...data.items);
      cursor = data.nextCursor;
      if (cursor === undefined) break;
    }
    // Las plantas son las de la primera pagina: el conjunto no cambia entre paginas.
    const { nextCursor: _ignored, ...rest } = first as T;
    return { ...rest, items, ...(cursor === undefined ? {} : { nextCursor: cursor }) } as T;
  }

  return {
    offers(session) {
      return paged(session, "/provider/offers", ProviderMultiSiteOffersResponse, new URLSearchParams());
    },
    trips(session, date) {
      return paged(session, "/provider/trips", ProviderMultiSiteTripsResponse, new URLSearchParams(date === undefined ? {} : { date }));
    },
    slotLoad(session) {
      return get(session, `/provider/slot-load?slotMinutes=${SLOT_MINUTES}`, ProviderSlotLoadResponse);
    },
    fleetPositions(session) {
      return get(session, "/provider/positions", ProviderFleetPositionsResponse);
    },
  };
}
